import {
  BaseAllotmentStatus,
  BaseAvailabilityStatus,
  TimeLinkageType
} from '@linktivity/ars-neo';
import type {
  ActivityCalendar,
  ActivityCalendarDailyAvailability,
  OptionProduct,
  TimeAvailability,
  TimeLinkage
} from '@linktivity/ars-neo';
import {
  addMinutesToTime,
  isStartTimeExpired,
  normalizeTimeValue
} from './date';
import { isSameDayLinkage } from './planInfo';

/**
 * True when the allotment can still be sold for the given status/quantity.
 * Moved out of SelectOption so the option card and drawer share one rule.
 */
export const hasSellableAllotment = (
  allotmentStatus?: BaseAllotmentStatus,
  allotmentQuantity?: number
): boolean => {
  if (!allotmentStatus) return true;

  if (allotmentStatus === BaseAllotmentStatus.AllotmentStatusUnlimited) {
    return true;
  }

  if (allotmentStatus === BaseAllotmentStatus.AllotmentStatusAllocable) {
    return (allotmentQuantity ?? 0) > 0;
  }

  return false;
};

/**
 * Daily-level "truly selectable" check: the day's availability status is OK and
 * its allotment is still sellable. No plan-level check — callers needing a
 * specific plan (e.g. the option drawer) layer that on top.
 */
export const isDayAvailable = (
  availability?: ActivityCalendarDailyAvailability
): boolean =>
  !!availability &&
  availability.status === BaseAvailabilityStatus.AvailabilityStatusOk &&
  hasSellableAllotment(
    availability.allotmentStatus,
    availability.allotmentQuantity
  );

export interface OptionDateEligibilityContext {
  timeLinkage?: TimeLinkage;
  /** Main product's selected date (YYYY-MM-DD); '' when not chosen / open-date. */
  mainTargetDate: string;
  /** Whether the main product's active plan is open-date. */
  mainIsOpenDate: boolean;
  /** Main product's validity expiration (YYYY-MM-DD); '' when no concrete bound. */
  mainValidityExpiration: string;
  /** Today (YYYY-MM-DD). */
  today: string;
}

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Whether an option date is eligible under the main-product linkage rules.
 * Date-range only — does NOT check stock.
 *
 * - same-day linkage -> only the main product's selected date
 * - open-date main   -> within [today, expiration]
 * - otherwise        -> any date
 */
export const isOptionDateEligible = (
  date: string,
  ctx: OptionDateEligibilityContext
): boolean => {
  if (isSameDayLinkage(ctx.timeLinkage)) {
    return !!ctx.mainTargetDate && date === ctx.mainTargetDate;
  }
  if (ctx.mainIsOpenDate) {
    if (date < ctx.today) return false;
    if (
      ISO_DATE.test(ctx.mainValidityExpiration) &&
      date > ctx.mainValidityExpiration
    ) {
      return false;
    }
    return true;
  }
  return true;
};

/**
 * Time window (HH:mm) an option's start time must fall within when it's
 * same-day-linked to the main product: [from, to], derived from the main
 * product's start time and the linkage offsets (minutes, both >= 0):
 *
 * - SAME_DAY_BEFORE_PARENT -> [main - offsetFrom, main - offsetTo]
 *   ("X 分前から Y 分前まで", offsetFrom = X >= offsetTo = Y)
 * - SAME_DAY / SAME_DAY_AFTER_PARENT -> [main + offsetFrom, main + offsetTo]
 *
 * Returns empty strings when there is no constraint (not same-day-linked, or
 * the main start time is unknown). A missing offset means 0 (proto3 omits
 * zeros), except a missing offsetTo on SAME_DAY / SAME_DAY_AFTER_PARENT, which
 * leaves `to` '' (no upper bound). A bound before 00:00 still sorts below every
 * real slot.
 */
export const computeOptionTimeWindow = (
  timeLinkage: TimeLinkage | undefined,
  mainStartTime: string
): { from: string; to: string } => {
  if (!isSameDayLinkage(timeLinkage) || !mainStartTime) {
    return { from: '', to: '' };
  }
  const base = normalizeTimeValue(mainStartTime);
  const offsetFromMinutes = timeLinkage?.offsetFromMinutes ?? 0;
  const offsetToMinutes = timeLinkage?.offsetToMinutes;
  if (timeLinkage?.type === TimeLinkageType.SameDayBeforeParent) {
    return {
      from: addMinutesToTime(base, -offsetFromMinutes),
      to: addMinutesToTime(base, -(offsetToMinutes ?? 0))
    };
  }
  return {
    from: addMinutesToTime(base, offsetFromMinutes),
    to: offsetToMinutes != null ? addMinutesToTime(base, offsetToMinutes) : ''
  };
};

/**
 * True only when the time is parseable AND falls outside the [from, to] window
 * produced by computeOptionTimeWindow (either bound may be '' = unbounded).
 * Normalization happens here so callers can pass raw values; an unparseable
 * time returns false so callers fail open instead of disabling/clearing on
 * bad data.
 */
export const isTimeOutsideWindow = (
  time: string,
  from: string,
  to: string
): boolean => {
  const normalized = normalizeTimeValue(time);
  if (!normalized) return false;
  return (!!from && normalized < from) || (!!to && normalized > to);
};

/**
 * Whether a single option is sold out for the given main-product date, under
 * the linkage + stock rules. Returns false (NOT sold out) when the answer is
 * inconclusive: the option is same-day-linked and the main date isn't chosen
 * yet, or its calendar hasn't loaded (no daily availabilities) — so callers
 * don't surface "sold out" before the data is in.
 */
export const isOptionSoldOutOnDate = (
  timeLinkage: TimeLinkage | undefined,
  dailyAvailabilities: ActivityCalendarDailyAvailability[],
  ctx: Omit<OptionDateEligibilityContext, 'timeLinkage'>
): boolean => {
  if (isSameDayLinkage(timeLinkage) && !ctx.mainTargetDate) return false;
  if (dailyAvailabilities.length === 0) return false;

  const eligibilityCtx = { ...ctx, timeLinkage };
  return !dailyAvailabilities.some(
    availability =>
      !!availability.date &&
      isOptionDateEligible(availability.date, eligibilityCtx) &&
      isDayAvailable(availability)
  );
};

/**
 * Whether every option product is sold out for the given main-product date.
 * An option whose calendar hasn't loaded yet is treated as inconclusive — NOT
 * sold out — so the caller doesn't warn before the data is in. Returns false
 * when there are no options.
 */
export const areAllOptionsSoldOutOnDate = (
  optionProducts: OptionProduct[],
  optionCalendars: Map<string, ActivityCalendar>,
  ctx: Omit<OptionDateEligibilityContext, 'timeLinkage'>
): boolean => {
  if (optionProducts.length === 0) return false;

  return optionProducts.every(option =>
    isOptionSoldOutOnDate(
      option.timeLinkage,
      optionCalendars.get(option.productContent?.id || '')
        ?.dailyAvailabilities || [],
      ctx
    )
  );
};

type TimePlanAvailability = NonNullable<
  TimeAvailability['planAvailabilities']
>[number];

/**
 * Whether a single plan-level entry on a time slot can still be sold: the
 * plan's status is OK and its allotment sellable. The single source for the
 * "slot sellable for a plan" rule — the any-plan slot check below and the
 * plan-scoped `isStartTimeBookable` (productPageSelectors) both build on it.
 */
export const isTimePlanAvailabilitySellable = (
  planAvailability?: TimePlanAvailability
): boolean =>
  planAvailability?.status === BaseAvailabilityStatus.AvailabilityStatusOk &&
  hasSellableAllotment(
    planAvailability.allotmentStatus,
    planAvailability.allotmentQuantity
  );

/**
 * Whether a single time slot is bookable. Mirrors the option drawer and
 * buildCollapsiblePlans: the slot's status is OK and — when it carries
 * plan-level entries — at least one plan is sellable. A slot with no
 * plan-level entries carries no plan constraint, so its OK time status
 * governs (consistent with how SelectOption / productPageSelectors treat missing
 * planAvailabilities); treating it as sold out here would falsely flag such
 * calendars.
 */
const isTimeSlotAvailable = (timeAvailability: TimeAvailability): boolean => {
  if (timeAvailability.status !== BaseAvailabilityStatus.AvailabilityStatusOk) {
    return false;
  }
  const planAvailabilities = timeAvailability.planAvailabilities || [];
  if (planAvailabilities.length === 0) return true;
  return planAvailabilities.some(plan => isTimePlanAvailabilitySellable(plan));
};

/**
 * Whether a single same-day-linked option has no bookable start time within the
 * linkage window for the main product's selected date AND start time. Returns
 * false (NOT sold out) when the answer is inconclusive or out of scope: the
 * option isn't same-day-linked (the main start time doesn't constrain it), the
 * main date/time isn't chosen yet, or its calendar hasn't loaded — so callers
 * don't surface "sold out" before the data is in. Reuses computeOptionTimeWindow
 * so this agrees with the option drawer's selectable-slot rule.
 */
export const isOptionSoldOutForMainTimeSlot = (
  timeLinkage: TimeLinkage | undefined,
  dailyAvailabilities: ActivityCalendarDailyAvailability[],
  ctx: Omit<OptionDateEligibilityContext, 'timeLinkage'> & {
    mainStartTime: string;
  }
): boolean => {
  if (!isSameDayLinkage(timeLinkage)) return false;
  if (!ctx.mainTargetDate || !ctx.mainStartTime) return false;
  if (dailyAvailabilities.length === 0) return false;

  const dayAvailability = dailyAvailabilities.find(
    availability => availability.date === ctx.mainTargetDate
  );
  // Eligible date carries no availability entry at all -> nothing to book.
  if (!dayAvailability) return true;

  const timeAvailabilities = dayAvailability.timeAvailabilities || [];
  // No per-time slots (e.g. a date-only same-day option): the time window
  // doesn't apply, so fall back to day-level availability rather than reporting
  // sold out just because there are no slots to check.
  if (timeAvailabilities.length === 0) return !isDayAvailable(dayAvailability);

  const { from, to } = computeOptionTimeWindow(timeLinkage, ctx.mainStartTime);

  // Sold out when no in-window, not-yet-started time slot is bookable. The
  // expiry check matters for BEFORE_PARENT, whose window precedes the main slot.
  return !timeAvailabilities.some(timeAvailability => {
    const slot = normalizeTimeValue(timeAvailability.time || '');
    if (!slot) return false;
    if (from && slot < from) return false;
    if (to && slot > to) return false;
    if (isStartTimeExpired(ctx.mainTargetDate, slot)) return false;
    return isTimeSlotAvailable(timeAvailability);
  });
};

/**
 * Whether every option product is sold out for the main product's selected date
 * AND start time, under the same-day-linkage time window. Mirrors
 * areAllOptionsSoldOutOnDate but at time-slot granularity. An option whose
 * calendar hasn't loaded, or which isn't same-day-linked, is treated as NOT sold
 * out, so any still-bookable option suppresses the warning. Returns false when
 * there are no options.
 */
export const areAllOptionsSoldOutForMainTimeSlot = (
  optionProducts: OptionProduct[],
  optionCalendars: Map<string, ActivityCalendar>,
  ctx: Omit<OptionDateEligibilityContext, 'timeLinkage'> & {
    mainStartTime: string;
  }
): boolean => {
  if (optionProducts.length === 0) return false;

  return optionProducts.every(option =>
    isOptionSoldOutForMainTimeSlot(
      option.timeLinkage,
      optionCalendars.get(option.productContent?.id || '')
        ?.dailyAvailabilities || [],
      ctx
    )
  );
};
